import { MouseEvent, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { VehicleDetails } from '../../../API/types/Vehicles';
import useVehicleThumbnail from '../../../theme/useVehicleThumbnail';
import useDriverThumbnail from '../../../theme/useDriverThumbnail';
import Tags from '../../../components/common/Tags';
import { ListItemWrapper } from '../../../components/ui/ListItemWrapper/ListItemWrapper';
import EntityLink from '../../../components/ui/EntityLink/EntityLink';
import { IconKeys } from '../../../components/common/Icon/IconKeys';
import { generateDistance } from '../../../utils/unitsUtils';
import { Button } from '../../../components/ui/Button/Button';
import { ThemeType } from '../../../theme/themeType';
import Modal from '../../../components/common/Modal/Modal';
import CheckBox from '../../../components/common/CheckBox/CheckBox';
import AssignDeviceToVehicle from '../Modals/AssignDeviceToVehicle/AssignDeviceToVehicle';
import useGetRole from '../../../API/hooks/useGetRole';
import {
  generateScoreColor,
  generateVehicleStatusColor,
  getScoreLabel,
  hasDisplayableEntityScore,
} from '../../../utils/commonUtils';
import { VehicleStatus } from '../../../constants/VehicleStatus';

type Props = {
  item?: VehicleDetails;
  isSelecting?: boolean;
  isSelected?: boolean;
  onSelect?: (checked: boolean, id: string) => void;
  lightVersion?: boolean;
  period?: string;
};

function VehiclesItem({
  item, lightVersion, isSelecting, isSelected, onSelect, period = 'monthly',
}: Props): JSX.Element {
  const theme = useTheme() as ThemeType;
  const vehicleThumbnail = useVehicleThumbnail();
  const driverThumbnail = useDriverThumbnail();
  const navigate = useNavigate();
  const { isFleetViewer } = useGetRole();
  const [modalState, toggleModalState] = useState(false);
  const {
    _id: id,
    model,
    manufacturer,
    driver,
    distanceInKmTotal,
    distanceInKmDaily,
    distanceInKmWeekly,
    distanceInKmMonthly,
    alertCountDaily,
    alertCountWeekly,
    alertCountMonthly,
    alertCountTotal,
    licensePlateNumber,
    fleetId,
    status,
    score,
    scoreDaily,
    scoreWeekly,
    scoreMonthly,
  } = item!;

  let displayScore: number | null;
  if (period === 'daily') displayScore = scoreDaily ?? null;
  else if (period === 'weekly') displayScore = scoreWeekly ?? null;
  else if (period === 'monthly') displayScore = scoreMonthly ?? null;
  else displayScore = score ?? null;

  let distance = distanceInKmTotal;
  if (period === 'daily') distance = distanceInKmDaily;
  else if (period === 'weekly') distance = distanceInKmWeekly;
  else if (period === 'monthly') distance = distanceInKmMonthly;

  let distanceForScore = distanceInKmTotal;
  if (period === 'daily') distanceForScore = distanceInKmDaily;
  else if (period === 'weekly') distanceForScore = distanceInKmWeekly;
  else if (period === 'monthly') distanceForScore = distanceInKmMonthly;
  const showScore = hasDisplayableEntityScore(displayScore, distanceForScore);

  let alerts: number | undefined = alertCountTotal;
  if (period === 'daily') alerts = alertCountDaily;
  else if (period === 'weekly') alerts = alertCountWeekly;
  else if (period === 'monthly') alerts = alertCountMonthly;

  const driverDetailsId = isSelecting ? undefined : (driver?._id || driver?.driverId);

  const handleAssignDriver = (e: MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    toggleModalState(true);
  };

  const handleClick = useCallback((): void => {
    if (isSelecting) {
      if (onSelect) {
        onSelect!(isSelected as boolean, id);
      }
      return;
    }

    navigate(`/vehicles/${id}`);
  }, [id, isSelecting, isSelected]);

  return (
    <>
      <ListItemWrapper
        onClick={handleClick}
      >
        {isSelecting && (
          <CheckBox
            label=""
            state={isSelected}
            onChange={() => {}}
            name={`vehicle-${id}`}
          />
        )}
        {showScore && (
          <Tags.Status label={getScoreLabel(displayScore!)} backgroundColor={generateScoreColor(displayScore!)} />
        )}
        {!showScore && (
          <Tags.Status label="No score" backgroundColor={theme.colors.outline} title="This vehicle didn't have any trips yet." />
        )}
        <Tags.ItemOverview
          avatar={vehicleThumbnail}
          avatarAlt="vehicle"
          title={licensePlateNumber}
          subtitle={`${manufacturer ? (`${manufacturer} `) : ''}${model ? (`${model} `) : ''}`}
        />
        <div className="tags-container" />
        {!!driver && (
          <EntityLink
            to={driverDetailsId ? `/drivers/${driverDetailsId}` : undefined}
            label={`Open driver ${driver.firstName} ${driver.lastName}`}
          >
            <Tags.Driver
              imageSrc={driver.avatarUrl || driverThumbnail}
              name={`${driver.firstName} ${driver.lastName}`}
              licenseName={driver.licenseName || ''}
            />
          </EntityLink>
        )}
        {!driver && !lightVersion && !isFleetViewer && (
          <Button
            height="52px"
            padding="24px 17px"
            width="200px"
            background={theme.colors.outline}
            hoverBackground={theme.colors.inactive}
            onClick={(e) => handleAssignDriver(e)}
          >
            Assign driver
          </Button>
        )}
        <Tags.Status
          label={status ? VehicleStatus[status] : 'No driving status'}
          backgroundColor={generateVehicleStatusColor(status)}
          title="Vehicle status"
          width="130px"
        />
        {alerts !== undefined && (
          <Tags.LabeledIcon
            label={`${alerts} alert${alerts === 1 ? '' : 's'}`}
            icon={IconKeys.ALERT}
          />
        )}
        <Tags.LabeledIcon
          label={generateDistance(distance, !['monthly', 'weekly', 'daily', 'all'].includes(period))}
          icon={IconKeys.DISTANCE}
        />
        <Tags.ProceedToDetails />
      </ListItemWrapper>
      {modalState && (
        <Modal
          title="Assign driver"
          state={modalState}
          toggleState={toggleModalState}
        >
          <AssignDeviceToVehicle
            onCancel={() => toggleModalState(false)}
            fleetId={fleetId}
            vehicleId={id}
            avatar={vehicleThumbnail}
            model={model}
            licensePlateNumber={licensePlateNumber}
          />
        </Modal>
      )}
    </>
  );
}

export default VehiclesItem;
